import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import MainNavigation from '../../components/ui/MainNavigation';
import FooterSection from '../homepage/components/FooterSection';
import NavigationBreadcrumb from '../../components/ui/NavigationBreadcrumb';
import SearchFilterSection from './components/SearchFilterSection';
import LeverancierGrid from './components/ProviderGrid';
import { supabase } from '../../lib/supabase';

const LeveranciersOverviewPage = () => {
  const [leveranciers, setLeveranciers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    searchQuery: '',
    category: 'all',
    location: '',
    certifiedOnly: false,
    minRating: 0
  });

  const breadcrumbItems = [
    { label: 'Home', path: '/homepage' },
    { label: 'Aanbieders', path: '/leveranciers' }
  ];

  useEffect(() => {
    fetchLeveranciers();
  }, []);

  const fetchLeveranciers = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch leveranciers with their service areas and certifications
      const { data: leveranciersData, error: leveranciersError } = await supabase?.from('user_profiles')?.select(`
          *,
          provider_service_areas(*),
          provider_certifications(*)
        `)?.eq('user_type', 'provider');

      if (leveranciersError) throw leveranciersError;

      // Fetch quote counts for each leverancier
      const leveranciersWithStats = await Promise.all(
        leveranciersData?.map(async (leverancier) => {
          const { count } = await supabase?.from('quote_requests')?.select('*', { count: 'exact', head: true })?.eq('provider_id', leverancier?.id)?.eq('status', 'completed');

          return {
            ...leverancier,
            completedProjects: count || 0,
            rating: (Math.random() * 1.5 + 3.5)?.toFixed(1), // Mock rating for now
            reviewCount: Math.floor(Math.random() * 300) + 50,
            responseTime: Math.random() > 0.5 ? '< 12 uur' : '< 24 uur',
            specialties: ['Zonnepanelen', 'Warmtepompen', 'Isolatie']?.slice(0, Math.floor(Math.random() * 3) + 1)
          };
        })
      );

      setLeveranciers(leveranciersWithStats || []);
    } catch (err) {
      console.error('Error fetching leveranciers:', err);
      setError(err?.message || 'Er is een fout opgetreden bij het ophalen van aanbieders');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
  };

  const filteredLeveranciers = leveranciers?.filter((leverancier) => {
    // Search query filter
    if (filters?.searchQuery) {
      const query = filters?.searchQuery?.toLowerCase();
      const matchesName = leverancier?.full_name?.toLowerCase()?.includes(query);
      const matchesSpecialties = leverancier?.specialties?.some(s => s?.toLowerCase()?.includes(query));
      if (!matchesName && !matchesSpecialties) return false;
    }

    // Location filter
    if (filters?.location) {
      const hasMatchingArea = leverancier?.provider_service_areas?.some(
        area => area?.province?.toLowerCase()?.includes(filters?.location?.toLowerCase()) ||
                area?.postal_code_range?.includes(filters?.location)
      );
      if (!hasMatchingArea) return false;
    }

    // Certified only filter
    if (filters?.certifiedOnly) {
      const hasActiveCertifications = leverancier?.provider_certifications?.some(
        cert => cert?.status === 'active'
      );
      if (!hasActiveCertifications) return false;
    }

    // Rating filter
    if (filters?.minRating > 0) {
      if (parseFloat(leverancier?.rating) < filters?.minRating) return false;
    }

    return true;
  });

  return (
    <div className="min-h-screen bg-background flex">
      <MainNavigation />
      <div className="flex-1 lg:ml-[72px]">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-primary/10 via-secondary/5 to-background py-12 sm:py-16 lg:py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <NavigationBreadcrumb items={breadcrumbItems} />
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center mt-8"
            >
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold text-foreground mb-4">
                Alle Aanbieders
              </h1>
              <p className="text-lg sm:text-xl text-muted-foreground max-w-3xl mx-auto">
                Ontdek en vergelijk gecertificeerde aanbieders voor uw verduurzamingsproject
              </p>
              
              <div className="flex flex-wrap items-center justify-center gap-6 mt-8">
                <div className="flex items-center space-x-2">
                  <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                    <span className="text-2xl font-bold text-primary">{leveranciers?.length}</span>
                  </div>
                  <span className="text-sm text-muted-foreground">Aanbieders</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-12 h-12 bg-success/10 rounded-full flex items-center justify-center">
                    <span className="text-2xl font-bold text-success">✓</span>
                  </div>
                  <span className="text-sm text-muted-foreground">Geverifieerd</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-12 h-12 bg-warning/10 rounded-full flex items-center justify-center">
                    <span className="text-2xl font-bold text-warning">★</span>
                  </div>
                  <span className="text-sm text-muted-foreground">Beoordeeld</span>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Search and Filter Section */}
        <SearchFilterSection 
          filters={filters}
          onFilterChange={handleFilterChange}
          totalResults={filteredLeveranciers?.length}
        />

        {/* Leverancier Grid */}
        <LeverancierGrid 
          leveranciers={filteredLeveranciers}
          loading={loading}
          error={error}
        />

        <FooterSection />
      </div>
    </div>
  );
};

export default LeveranciersOverviewPage;